import {getPriceForCountry} from '../../../../features/CatalogGrid/utils/getPriceForCountry'
import {resolveCartItemQuantity} from '../../../../features/CatalogGrid/utils/buildSendLinkSubscriptionGroups'
import {KEAP_ADDONS_PARENT_CODES} from '../../../../utils/constants'

// Builds the body for POST cc/order/keap-partner/generate-link from the shared
// catalog cart (useCatalogCart items). The wire format is the one the legacy
// 1:Many page (kp-11Link-generateUrl) has always sent, because the backend
// prices the order from these objects:
//   planCode    – centers as priced objects (unitAmount / amount / addons)
//   addOnCode   – apps as {code, quantity, addons:[{code, quantity}]}, unpriced
//   oneTimeFees – fees as priced objects (the UI refuses fees with no price)
// plus coupon / incentive codes and partner identifiers.

const money = value => Number(value || 0).toFixed(2)

const lineUnitPrice = item => {
  const quantity = item.quantity || 1
  return Number(item.unitPrice ?? 0) / quantity
}

const toPricedAddon = (addon, rawByCode) => {
  const raw = rawByCode.get(addon.productCode)
  const unit = Number(addon.unitPrice ?? 0) / (addon.quantity || 1)
  return {
    code: addon.productCode,
    name: addon.productName ?? raw?.name,
    ...(raw?.id ? {id: raw.id} : {}),
    quantity: addon.quantity ?? 1,
    unitAmount: unit,
    amount: unit,
  }
}

// Per-center custom fields exactly as the legacy page sent them: Marketing
// Center carries the website domain (blank when the rep leaves it for the
// customer to fill in at checkout); every other center sends an empty object.
// Signature plans carry the rep's Onboarding Yes/No (the catalog's own
// `onboarding` field, default no). The client portal reads it to decide whether
// to ask the customer to schedule onboarding.
const ONBOARDING_CHOICE_PLAN_CODES = [
  'mc_prov2-5-thryv-m2m',
  'signature-5-thryv-annual',
  'signature_plus-5-thryv-m2m',
  'signature_plus-5-thryv-annual',
]

const toCustomFields = item => {
  const code = String(item.productCode || '')
  return {
    ...(code.startsWith('mc_')
      ? {mcDomain: item.configuration?.domain ?? ''}
      : {}),
    ...(ONBOARDING_CHOICE_PLAN_CODES.includes(code)
      ? {
          includeOnboarding: {
            value: item.configuration?.onboarding === 'yes' ? 'Yes' : 'No',
          },
        }
      : {}),
  }
}

const toCenter = (item, {rawByCode}) => {
  const raw = rawByCode.get(item.productCode)
  const unit = lineUnitPrice(item)
  return {
    code: item.productCode,
    name: item.productName ?? raw?.name,
    ...(raw?.id ? {id: raw.id} : {}),
    quantity: item.quantity ?? 1,
    unitAmount: money(unit),
    amount: Number(unit),
    addons: (item.addons ?? []).map(a => toPricedAddon(a, rawByCode)),
    customFields: toCustomFields(item),
  }
}

// Tiered apps (Keap Additional Contacts / Text & Voice) keep item.quantity at 1
// and carry the tier in configuredOptions.optionValue — resolve it so the portal
// and Recurly price the picked tier instead of Tier 1.
const toAppShape = item => ({
  code: item.productCode,
  quantity: resolveCartItemQuantity(item),
  ...(item.addons?.length
    ? {
        addons: item.addons.map(addon => ({
          code: addon.productCode,
          quantity: addon.quantity ?? 1,
        })),
      }
    : {}),
})

const isKeapWrapper = code => KEAP_ADDONS_PARENT_CODES.includes(code)

// Same wire format the legacy 1:Many page sent (kp-11Link-generateUrl): Keap
// add-ons (Additional Contacts, Additional User, Text & Voice, ...) nest under
// the Keap Add-Ons wrapper plan. Sent flat, ooecp can't tier-price them and
// shows Additional Contacts $0.00 / Text & Voice Tier 1.
const toAddOnCode = (apps, productConfigInfo) => {
  const catalogParentByCode = new Map(
    (productConfigInfo.apps ?? [])
      .filter(app => isKeapWrapper(app.parentProduct))
      .map(app => [app.code, app.parentProduct]),
  )
  // Children come from the catalog's parentProduct (products.parent_prod_code),
  // not a hardcoded code list — the set of Keap add-ons changes often.
  const isKeapChild = item =>
    !isKeapWrapper(item.productCode) &&
    catalogParentByCode.has(item.productCode)
  const children = apps.filter(isKeapChild)
  if (!children.length) return apps.map(toAppShape)

  const wrapper = apps.find(item => isKeapWrapper(item.productCode))
  const wrapperCode =
    wrapper?.productCode ??
    catalogParentByCode.get(children[0].productCode) ??
    KEAP_ADDONS_PARENT_CODES[0]
  const others = apps.filter(
    item => !isKeapChild(item) && !isKeapWrapper(item.productCode),
  )
  return [
    ...others.map(toAppShape),
    {
      code: wrapperCode,
      quantity: 1,
      addons: [
        ...(wrapper?.addons ?? []).map(addon => ({
          code: addon.productCode,
          quantity: addon.quantity ?? 1,
        })),
        ...children.map(child => ({
          code: child.productCode,
          quantity: resolveCartItemQuantity(child),
        })),
      ],
    },
  ]
}

const toFee = (item, {rawByCode, country}) => {
  const raw = rawByCode.get(item.productCode)
  // getPriceForCountry answers 0 when the catalog has no price for the
  // country, which would turn an unpriced fee into a free one — only trust it
  // when the catalog actually lists currencies for the fee.
  const catalogPrice = raw?.currencies?.length
    ? getPriceForCountry(raw.currencies, country)
    : undefined
  const unit = item.unitPrice ?? catalogPrice
  return {
    code: item.productCode,
    name: item.productName ?? raw?.name,
    ...(raw?.id ? {id: raw.id} : {}),
    prodType: 'OneTimeFees',
    quantity: item.quantity ?? 1,
    unitAmount: unit == null ? undefined : money(unit),
    amount: unit == null ? undefined : money(unit),
    img: null,
    groupKey: 'oneTimeFees',
  }
}

export function buildManyLinkPayload({
  items = [],
  productConfigInfo = {},
  country = 'US',
  keapPartnerId,
  sfPartnerAccountId,
  partnerEmail,
  couponCodes = [],
  incentiveCodes = [],
  influencerName,
  bundleId,
}) {
  const rawByCode = new Map(
    [
      ...(productConfigInfo.centers ?? []),
      ...(productConfigInfo.apps ?? []),
      ...(productConfigInfo.oneTimeFees ?? []),
    ].map(product => [product.code, product]),
  )
  const ctx = {rawByCode, country}

  const payload = {
    sfPartnerAccountId: sfPartnerAccountId || '123121',
    keapPartnerId: keapPartnerId ? String(keapPartnerId) : keapPartnerId,
    countryCode: country,
    // Tells the backend this is a 1:Many link so it can refuse SEO / Thryv
    // Leads / LeadsLift.
    linkType: '1:many',
    planCode: items
      .filter(item => item.productType === 'CENTER')
      .map(item => toCenter(item, ctx)),
    addOnCode: toAddOnCode(
      items.filter(item => item.productType === 'APP'),
      productConfigInfo,
    ),
    oneTimeFees: items
      .filter(item => item.productType === 'ONE_TIME_FEE')
      .map(item => toFee(item, ctx)),
    partnerEmail,
    couponCode: couponCodes,
  }
  if (incentiveCodes.length) payload.defaultIncentive = incentiveCodes
  if (influencerName) payload.influencerName = influencerName
  if (bundleId) payload.slp_share_link_id = bundleId
  return payload
}
